import { X, Plus, Minus } from 'lucide-react';
import { CartItem as CartItemType } from '@/types';
import { formatPrice } from '@/lib/utils';
import { calculateCartItemDiscount } from '@/lib/discount';
import { Link } from 'react-router-dom';

interface CartItemProps {
  item: CartItemType;
  onQuantityChange: (id: number, quantity: number) => void;
  onVariationQuantityChange: (itemId: number, variation: string, change: number) => void;
  onRemoveVariation: (itemId: number, variation: string) => void;
  onRemoveFromCart: (id: number) => void;
  soldOutItems: number[];
  stockLevels: Record<number, { total: number; variations: Record<string, number> }>;
}

export default function CartItem({
  item,
  onQuantityChange,
  onVariationQuantityChange,
  onRemoveVariation,
  onRemoveFromCart,
  soldOutItems,
  stockLevels,
}: CartItemProps) {
  const isSoldOut = soldOutItems.includes(item.id);
  const stockLevel = stockLevels[item.id];
  const { totalOriginalPrice, totalDiscountValue, totalFinalPrice, discountInfo } = calculateCartItemDiscount(item);

  const getVariationAmount = (variation: string) => {
    const variationData = item.variations.find(v => v.variation === variation);
    return variationData ? parseInt(variationData.variation_amount) : 0;
  };

  const calculateVariationTotal = (variation: string, quantity: number) => {
    const amount = getVariationAmount(variation);
    return amount * quantity;
  };

  return (
    <li className={`p-6 ${isSoldOut ? 'opacity-50' : ''}`}>
      <div className="flex items-start gap-4">
        <Link to={`/product/${item.id}`} className="relative flex-shrink-0">
          <img
            src={item.images[0]}
            alt={item.produt_name}
            className="w-24 h-24 object-cover rounded-lg"
          />
          {isSoldOut && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
              <span className="text-white font-bold text-sm">SOLD OUT</span>
            </div>
          )}
        </Link>

        <div className="flex-1 min-w-0">
          <div className="flex justify-between">
            <Link to={`/product/${item.id}`}>
              <h3 className="text-base font-medium text-gray-900 dark:text-white truncate hover:text-brand-primary dark:hover:text-brand-light transition-colors">
                {item.produt_name}
              </h3>
            </Link>
            <button
              onClick={() => onRemoveFromCart(item.id)}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {item.selectedVariations ? (
            <div className="mt-2 space-y-2">
              {(() => {
                const minimumOrder = item.minimum_order || 1;
                const totalVariationQuantity = item.selectedVariations?.reduce((sum, v) => sum + v.quantity, 0) || 0;
                if (minimumOrder > 1) {
                  return (
                    <div className={`mb-2 text-xs font-medium ${
                      totalVariationQuantity >= minimumOrder 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-yellow-600 dark:text-yellow-400'
                    }`}>
                      {totalVariationQuantity >= minimumOrder 
                        ? `✓ Minimum order met (${totalVariationQuantity}/${minimumOrder})`
                        : `Minimum order: ${minimumOrder} (Current: ${totalVariationQuantity})`
                      }
                    </div>
                  );
                }
                return null;
              })()}
              {item.selectedVariations.map((variation) => (
                <div key={variation.variation} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {variation.variation}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      ({formatPrice(getVariationAmount(variation.variation))})
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {(() => {
                      const minimumOrder = item.minimum_order || 1;
                      const totalVariationQuantity = item.selectedVariations?.reduce((sum, v) => sum + v.quantity, 0) || 0;
                      const isAtMinimum = totalVariationQuantity <= minimumOrder && variation.quantity <= 1;
                      
                      return (
                        <button
                          onClick={() => onVariationQuantityChange(item.id, variation.variation, -1)}
                          disabled={isSoldOut || isAtMinimum}
                          className="p-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                          title={isAtMinimum ? `Minimum order is ${minimumOrder}` : ''}
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                      );
                    })()}
                    <span className="w-8 text-center text-gray-900 dark:text-white">
                      {variation.quantity}
                    </span>
                    <button
                      onClick={() => onVariationQuantityChange(item.id, variation.variation, 1)}
                      disabled={isSoldOut || (stockLevel?.variations[variation.variation] || 0) <= variation.quantity}
                      className="p-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onRemoveVariation(item.id, variation.variation)}
                      className="ml-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <span className="text-sm font-medium text-gray-900 dark:text-white ml-4">
                      = {formatPrice(calculateVariationTotal(variation.variation, variation.quantity))}
                    </span>
                  </div>
                </div>
              ))}
              
              {discountInfo.hasDiscount && (
                <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-yellow-800 dark:text-yellow-400">
                      Bulk Discount ({discountInfo.threshold}+ items):
                    </span>
                    <span className="font-medium text-yellow-800 dark:text-yellow-400">
                      {discountInfo.discountType === 'percentage' 
                        ? `${discountInfo.discountAmount}% OFF`
                        : `${formatPrice(discountInfo.discountAmount)} OFF each`
                      }
                    </span>
                  </div>
                  {discountInfo.isEligible ? (
                    <div className="mt-2 flex justify-between items-center text-sm">
                      <span className="text-green-700 dark:text-green-400 font-medium">
                        ✓ Discount Applied
                      </span>
                      <span className="text-green-700 dark:text-green-400 font-bold">
                        Save {formatPrice(totalDiscountValue)}
                      </span>
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-300">
                      Add {discountInfo.threshold - discountInfo.currentQuantity} more to qualify
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-2">
              {(() => {
                const minimumOrder = item.minimum_order || 1;
                if (minimumOrder > 1) {
                  return (
                    <div className={`mb-2 text-xs font-medium ${
                      item.quantity >= minimumOrder 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-yellow-600 dark:text-yellow-400'
                    }`}>
                      {item.quantity >= minimumOrder 
                        ? `✓ Minimum order met (${item.quantity}/${minimumOrder})`
                        : `Minimum order: ${minimumOrder} (Current: ${item.quantity})`
                      }
                    </div>
                  );
                }
                return null;
              })()}
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Price: {discountInfo.isEligible ? (
                      <>
                        <span className="line-through">{formatPrice(parseInt(item.sellingprice))}</span>
                        <span className="ml-2 text-brand-primary dark:text-brand-light font-bold">
                          {formatPrice(discountInfo.finalPrice)}
                        </span>
                      </>
                    ) : (
                      formatPrice(parseInt(item.sellingprice))
                    )}
                  </span>
                  {discountInfo.hasDiscount && (
                    <span className="text-xs text-yellow-600 dark:text-yellow-400">
                      {discountInfo.isEligible 
                        ? `Discount applied!`
                        : `Buy ${discountInfo.threshold}+ for ${discountInfo.discountType === 'percentage' ? `${discountInfo.discountAmount}%` : formatPrice(discountInfo.discountAmount)} off`
                      }
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {(() => {
                    const minimumOrder = item.minimum_order || 1;
                    const isAtMinimum = item.quantity <= minimumOrder;
                    
                    return (
                      <button
                        onClick={() => onQuantityChange(item.id, item.quantity - 1)}
                        disabled={isSoldOut || isAtMinimum}
                        className="p-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                        title={isAtMinimum ? `Minimum order is ${minimumOrder}` : ''}
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                    );
                  })()}
                  <span className="w-8 text-center text-gray-900 dark:text-white">
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => onQuantityChange(item.id, item.quantity + 1)}
                    disabled={isSoldOut || (stockLevel?.total || 0) <= item.quantity}
                    className="p-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <span className="text-sm font-medium text-gray-900 dark:text-white ml-4">
                    = {formatPrice(totalFinalPrice)}
                  </span>
                </div>
              </div>
              
              {discountInfo.hasDiscount && (
                <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-yellow-800 dark:text-yellow-400">
                      Bulk Discount ({discountInfo.threshold}+ items):
                    </span>
                    <span className="font-medium text-yellow-800 dark:text-yellow-400">
                      {discountInfo.discountType === 'percentage' 
                        ? `${discountInfo.discountAmount}% OFF`
                        : `${formatPrice(discountInfo.discountAmount)} OFF each`
                      }
                    </span>
                  </div>
                  {discountInfo.isEligible ? (
                    <div className="mt-2 flex justify-between items-center text-sm">
                      <span className="text-green-700 dark:text-green-400 font-medium">
                        ✓ Discount Applied
                      </span>
                      <span className="text-green-700 dark:text-green-400 font-bold">
                        Save {formatPrice(totalDiscountValue)}
                      </span>
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-300">
                      Add {discountInfo.threshold - discountInfo.currentQuantity} more to qualify
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}